import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Upload } from "lucide-react";

import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  notarialDocumentTypeTranslationKey,
  type NotarialCertificateLabelContext,
} from "@/lib/notarialDocumentTypeLabel";
import type { RequestDocumentSlot } from "@/services/userNotarialRequestsService";

export interface DocumentSlotsListProps {
  slots: RequestDocumentSlot[];
  /** Which document vocabulary to translate typed slots with. */
  labelContext: NotarialCertificateLabelContext;
  onUpload: (slotId: string, file: File | null) => void;
  /** The slot currently uploading, so only its own row shows progress. */
  uploadingSlotId: string | null;
  disabled?: boolean;
}

/**
 * What the professional asked this consumer for, one row per request
 * (spec request-document-slots §3c).
 *
 * The point of the slot is that the consumer picks a FILE and nothing else:
 * the document type comes from the row, so there is no type dropdown here and
 * no way to misfile what was asked for. That is why uploading starts the
 * moment a file is chosen — a confirm step would add a click and decide
 * nothing.
 *
 * Fulfilled rows stay listed rather than disappearing, because "2 de 4" is
 * the thing the consumer wants to know and a list that only shows what is
 * left cannot say it.
 */
export function DocumentSlotsList({
  slots,
  labelContext,
  onUpload,
  uploadingSlotId,
  disabled = false,
}: DocumentSlotsListProps): JSX.Element | null {
  const { t } = useTranslation();
  if (slots.length === 0) return null;

  const fulfilledCount = slots.filter(
    (slot) => slot.status === "fulfilled",
  ).length;

  return (
    <section className="mb-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          {t("requests.user.documentSlotsTitle")}
        </h3>
        <span className="text-xs tabular-nums text-muted-foreground">
          {t("requests.user.documentSlotsProgress", {
            done: fulfilledCount,
            total: slots.length,
          })}
        </span>
      </header>

      <ul className="space-y-2">
        {slots.map((slot) => (
          <DocumentSlotRow
            key={slot.id}
            slot={slot}
            labelContext={labelContext}
            onUpload={onUpload}
            isUploading={uploadingSlotId === slot.id}
            disabled={disabled || uploadingSlotId != null}
          />
        ))}
      </ul>
    </section>
  );
}

function DocumentSlotRow({
  slot,
  labelContext,
  onUpload,
  isUploading,
  disabled,
}: {
  slot: RequestDocumentSlot;
  labelContext: NotarialCertificateLabelContext;
  onUpload: (slotId: string, file: File | null) => void;
  isUploading: boolean;
  disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  // Typed or custom, never both — the database enforces it, so the label is
  // read off whichever one is present rather than preferred and defaulted.
  const label =
    slot.documentType != null
      ? t(
          notarialDocumentTypeTranslationKey(slot.documentType, labelContext),
          slot.documentType,
        )
      : (slot.customLabel ?? "");
  const inputId = `document-slot-${slot.id}`;

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-3">
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {slot.why ? (
          <p className="text-xs text-muted-foreground">{slot.why}</p>
        ) : null}
      </div>

      {slot.status === "fulfilled" ? (
        <StatusBadge variant="success">
          <CheckCircle2 className="mr-1 size-3" aria-hidden />
          {t("requests.user.documentSlotUploaded")}
        </StatusBadge>
      ) : (
        // A label over a hidden input rather than a button: one click opens
        // the file picker, and the picker is the only decision left.
        <label
          htmlFor={inputId}
          className={`inline-flex h-9 shrink-0 cursor-pointer items-center rounded-md border border-input bg-background px-3 text-sm font-medium transition-colors duration-150 hover:bg-accent hover:text-accent-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${
            disabled ? "pointer-events-none opacity-50" : ""
          }`}
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
              {t("requests.user.uploadingDocument")}
            </>
          ) : (
            <>
              <Upload className="mr-2 size-4" aria-hidden />
              {t("requests.user.documentSlotUploadAction")}
            </>
          )}
          <input
            id={inputId}
            type="file"
            className="sr-only"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            disabled={disabled}
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              // Cleared so choosing the SAME file again still fires change,
              // which is the retry path after a failed upload.
              event.target.value = "";
              onUpload(slot.id, file);
            }}
          />
        </label>
      )}
    </li>
  );
}
